import React, { useEffect, useState } from "react";
import logger from "../../logger";
import {
  Button,
  Text,
  MessageBar,
  MessageBarBody,
  Dropdown,
  Option,
  Tooltip,
} from "@fluentui/react-components";
import {
  ArrowSyncRegular,
  AddRegular,
  TableAddRegular,
  DeleteRegular,
} from "@fluentui/react-icons";
import { fetchAllReports, fetchSelectedReport } from "./reportService";
import { fetchAllCompanies } from "../company/companyService";
import { useReportManagementStyles } from "./ReportManagement.styles";

const ReportManagement = () => {
  const [excelData, setExcelData] = useState("");
  const [excelError, setExcelError] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [reports, setReports] = useState([]);
  const [selectedReportId, setSelectedReportId] = useState("");
  const [reportsLoading, setReportsLoading] = useState(false);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [dropdownKey, setDropdownKey] = useState(0);
  const [companyDropdownKey, setCompanyDropdownKey] = useState(0);
  const [reportDataRange, setReportDataRange] = useState(null);
  const styles = useReportManagementStyles();

  const companyNameToIdMap = React.useMemo(() => {
    const map = {};
    companies.forEach((company) => {
      const companyName = company.name || company.id;
      map[companyName] = company.id;
    });
    return map;
  }, [companies]);

  const selectedCompanyDisplayName = React.useMemo(() => {
    if (!selectedCompanyId) return undefined;
    const company = companies.find((c) => c.id === selectedCompanyId);
    return company ? company.name || company.id : undefined;
  }, [selectedCompanyId, companies]);

  const reportNameToIdMap = React.useMemo(() => {
    const map = {};
    reports.forEach((report) => {
      const reportName = report.reportName || report.reportId;
      map[reportName] = report.reportId;
    });
    return map;
  }, [reports]);

  const selectedReportDisplayName = React.useMemo(() => {
    if (!selectedReportId) return undefined;
    const report = reports.find((r) => r.reportId === selectedReportId);
    return report ? report.reportName || report.reportId : undefined;
  }, [selectedReportId, reports]);

  useEffect(() => {
    loadCompanies();
    loadReports();
  }, []);

  const loadCompanies = async () => {
    try {
      setCompaniesLoading(true);
      const companiesData = await fetchAllCompanies();
      setCompanies(companiesData);
    } catch (error) {
      logger.error("Error loading companies:", error);
      setExcelData("Error loading companies: " + (error.message || "Unknown error"));
      setExcelError(true);
      setTimeout(() => {
        setExcelData("");
      }, 3000);
    } finally {
      setCompaniesLoading(false);
    }
  };

  const loadReports = async (companyId) => {
    try {
      setReportsLoading(true);
      const reportsData = await fetchAllReports(companyId);
      logger.info("Reports data:", reportsData);
      setReports(reportsData);
    } catch (error) {
      logger.error("Error loading reports:", error);
      setExcelData("Error loading reports: " + (error.message || "Unknown error"));
      setExcelError(true);
      setTimeout(() => {
        setExcelData("");
      }, 3000);
    } finally {
      setReportsLoading(false);
    }
  };

  const insertReportDataToExcel = async (reportsData) => {
    try {
      await Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getActiveWorksheet();

        if (!reportsData || reportsData.length === 0) {
          throw new Error("No report data to insert");
        }

        const selectedRange = context.workbook.getSelectedRange();
        selectedRange.load("rowIndex, columnIndex");
        await context.sync();

        const startRow = selectedRange.rowIndex + 1;
        const startColIndex = selectedRange.columnIndex;

        const reportHeaders = [
          "Company Id",
          "Company Name",
          "Report Id",
          "Report Name",
          "Report Status",
          "Metric Reference",
          "Reporting Period",
          "Reporting Frequency",
        ];

        const reportResult = [reportHeaders];

        if (reportsData.length > 0) {
          logger.info("First report item:", reportsData[0]);
          logger.info("First report metricReferences:", reportsData[0]?.metricReferences);
        }

        reportsData.forEach((item) => {
          const status = (item.reportStatus || "").replace(/([a-z])([A-Z])/g, "$1 $2");

          if (item.metricReferences && item.metricReferences.length > 0) {
            item.metricReferences.forEach((metricReference) => {
              reportResult.push([
                item.companyId || "",
                item.companyName || "",
                item.reportId || "",
                item.reportName || "",
                status,
                metricReference || "",
                item.reportingPeriod || "",
                item.reportingFrequency || "",
              ]);
            });
          } else {
            reportResult.push([
              item.companyId || "",
              item.companyName || "",
              item.reportId || "",
              item.reportName || "",
              status,
              "",
              item.reportingPeriod || "",
              item.reportingFrequency || "",
            ]);
          }
        });

        const getColumnLetter = (colNum) => {
          let result = "";
          let num = colNum;
          while (num > 0) {
            num--;
            result = String.fromCharCode(65 + (num % 26)) + result;
            num = Math.floor(num / 26);
          }
          return result;
        };

        const reportStartCol = getColumnLetter(startColIndex + 1);
        const reportEndCol = getColumnLetter(startColIndex + reportHeaders.length);
        const reportEndRow = startRow + reportResult.length - 1;

        const reportRange = sheet.getRange(
          `${reportStartCol}${startRow}:${reportEndCol}${reportEndRow}`
        );
        reportRange.values = reportResult;

        const reportHeaderRange = sheet.getRange(
          `${reportStartCol}${startRow}:${reportEndCol}${startRow}`
        );
        reportHeaderRange.format.fill.color = "#1aebff";
        reportHeaderRange.format.font.color = "black";
        reportHeaderRange.format.font.bold = true;

        reportRange.format.autofitColumns();

        await context.sync();

        setReportDataRange({
          startCol: reportStartCol,
          endCol: reportEndCol,
          startRow,
          endRow: reportEndRow,
        });

        setExcelData("Report & metrics data inserted successfully!");
        setExcelError(false);

        setTimeout(() => setExcelData(""), 3000);
      });
    } catch (error) {
      logger.error("Error inserting report data to Excel:", error);
      setExcelData("Error inserting report data to Excel: " + (error.message || "Unknown error"));
      setExcelError(true);
    }
  };

  const clearReportDataRange = async () => {
    try {
      if (!reportDataRange) {
        setExcelData("No report data range found to clear.");
        setExcelError(true);
        setTimeout(() => {
          setExcelData("");
        }, 3000);
        return;
      }

      await Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getActiveWorksheet();

        const { startCol, endCol, startRow, endRow } = reportDataRange;
        const fullRange = sheet.getRange(`${startCol}${startRow}:${endCol}${endRow}`);

        fullRange.clear(Excel.ClearApplyTo.all);

        await context.sync();
      });
    } catch (error) {
      logger.error("Error clearing report data range:", error);
      throw error;
    }
  };

  const handleCompanySelect = async (companyId) => {
    setSelectedCompanyId(companyId);
    setSelectedReportId("");
    setDropdownKey((prev) => prev + 1);

    // Fetch reports filtered by company ID
    await loadReports(companyId);
  };

  const handleClearAll = async () => {
    setSelectedCompanyId("");
    setSelectedReportId("");
    setDropdownKey((prev) => prev + 1);
    setCompanyDropdownKey((prev) => prev + 1);

    // Reload all reports without company filter
    await loadReports();

    try {
      await clearReportDataRange();
      setReportDataRange(null);
      setExcelData("Report data cleared successfully!");
      setExcelError(false);
      setTimeout(() => {
        setExcelData("");
      }, 3000);
    } catch (error) {
      logger.error("Error clearing report data:", error);
      setExcelData("Error clearing report data: " + (error.message || "Unknown error"));
      setExcelError(true);
      setTimeout(() => {
        setExcelData("");
      }, 3000);
    }
  };

  const handleSelectedReportGet = async () => {
    if (!selectedReportId) {
      setExcelData("Please select a report first");
      setExcelError(true);
      setTimeout(() => {
        setExcelData("");
      }, 3000);
      return;
    }

    try {
      setReportsLoading(true);
      const reportData = await fetchSelectedReport(selectedReportId);
      if (reportData && typeof reportData === "object" && !Array.isArray(reportData)) {
        await insertReportDataToExcel([reportData]);
      } else {
        setExcelData("No report data found");
        setExcelError(true);
        setTimeout(() => {
          setExcelData("");
        }, 3000);
      }
    } catch (error) {
      setExcelData("Error fetching selected report: " + (error.message || "Unknown error"));
      setExcelError(true);
      setTimeout(() => {
        setExcelData("");
      }, 3000);
    } finally {
      setReportsLoading(false);
    }
  };

  const handleInsertAllReports = async () => {
    if (reports.length === 0) {
      setExcelData("No reports available. Please fetch reports first.");
      setExcelError(true);
      setTimeout(() => {
        setExcelData("");
      }, 3000);
      return;
    }

    await insertReportDataToExcel(reports);
  };

  return (
    <div className={styles.container}>
      <div className={styles.dropdownContainer}>
        <Text as="p" className={styles.label}>
          Select a Company:
        </Text>
        <Dropdown
          positioning="below-start"
          inlinePopup
          key={companyDropdownKey}
          placeholder={companiesLoading ? "Loading companies..." : "Select a company"}
          value={selectedCompanyDisplayName || ""}
          disabled={companiesLoading}
          onOptionSelect={(event, data) => {
            const selectedCompanyName = data.optionValue || "";
            const companyId = companyNameToIdMap[selectedCompanyName] || "";
            handleCompanySelect(companyId);
          }}
          className={styles.dropdown}
        >
          {companies.map((company) => {
            const companyName = company.name || company.id;
            return (
              <Option key={company.id} value={companyName}>
                {companyName}
              </Option>
            );
          })}
        </Dropdown>
      </div>

      <Text
        as="p"
        style={{
          fontSize: "12px",
          color: "#605e5c",
          fontStyle: "italic",
          marginTop: "-8px",
          marginBottom: "12px",
        }}
      >
        Note: Please select a company first to view and filter reports associated with that company.
      </Text>

      <div className={styles.dropdownContainer}>
        <Text as="p" className={styles.label}>
          Select a Report:
        </Text>
        <Dropdown
          positioning="below-start"
          inlinePopup
          key={dropdownKey}
          placeholder={reportsLoading ? "Loading reports..." : "Select a report"}
          value={selectedReportDisplayName || ""}
          disabled={reportsLoading}
          onOptionSelect={(event, data) => {
            const selectedReportName = data.optionValue || "";
            const reportId = reportNameToIdMap[selectedReportName] || "";
            setSelectedReportId(reportId);
          }}
          className={styles.dropdown}
        >
          {reports.map((report) => {
            const reportName = report.reportName || report.reportId;
            return (
              <Option key={report.reportId} value={reportName}>
                {reportName}
              </Option>
            );
          })}
        </Dropdown>
      </div>

      <div className={styles.buttonsContainer}>
        <Tooltip
          withArrow
          content={{ children: "Refetch Data", className: styles.tooltip }}
          relationship="label"
          positioning="below"
        >
          <Button
            appearance="secondary"
            onClick={() => loadReports(selectedCompanyId)}
            disabled={reportsLoading || companiesLoading}
            className={styles.refetchButton}
            icon={<ArrowSyncRegular />}
          />
        </Tooltip>
        <Tooltip
          withArrow
          content={{ children: "Insert Report", className: styles.tooltip }}
          relationship="label"
          positioning="below"
        >
          <Button
            appearance="primary"
            onClick={handleSelectedReportGet}
            disabled={reportsLoading || companiesLoading || !selectedReportId}
            className={styles.insertButton}
            icon={<AddRegular />}
          />
        </Tooltip>
        {reports.length > 0 && (
          <Tooltip
            withArrow
            content={{ children: "Insert All Reports", className: styles.tooltip }}
            relationship="label"
            positioning="below"
          >
            <Button
              appearance="primary"
              onClick={handleInsertAllReports}
              disabled={reportsLoading || companiesLoading}
              className={styles.insertAllButton}
              icon={<TableAddRegular />}
            />
          </Tooltip>
        )}
        <Tooltip
          withArrow
          content={{ children: "Clear Report Data", className: styles.tooltip }}
          relationship="label"
          positioning="below"
        >
          <Button
            appearance="secondary"
            onClick={handleClearAll}
            disabled={reportsLoading || companiesLoading}
            className={styles.clearButton}
            icon={<DeleteRegular />}
          />
        </Tooltip>
      </div>

      {excelData && (
        <MessageBar
          intent={excelError ? "error" : "success"}
          className={excelError ? styles.errorMessage : styles.successMessage}
        >
          <MessageBarBody>{excelData}</MessageBarBody>
        </MessageBar>
      )}
    </div>
  );
};

export default ReportManagement;
